<template>
	<div
		v-if="internalIsMobileLandscape"
		class="multi-format-tool__rotate-message"
	>
		<Icon
			icon-name="restore-1"
			size="xxl"
		/>
		{{ $I18n.trans('commons.rotate_device') }}
	</div>
	<div
		v-else
		class="multi-format-tool"
		:class="[
			`is-${currentUiStatus}`,
			{ 'is-community-cropping': isCommunityCropping },
			{ 'is-mobile': internalIsMobile },
			{ 'is-mobile-layout': isMobileViewport },
		]"
	>
		<ModalKeyboardShortcuts
			v-if="isCommunityCropping"
			:modal-name="modalNameKeyboardShortcuts"
		/>
		<ModalConfirm
			:modal-name="modalNameAspectRatioChange"
			:title="$I18n.trans('event.multiformat_editor.messages.confirm_format_change_title')"
			:text="$I18n.trans('event.multiformat_editor.messages.confirm_format_change')"
			:confirm-button-text="$I18n.trans('event.multiformat_editor.messages.confirm_format_change_button')"
			@confirmed="confirmAspectRatioChange"
			@cancelled="pendingAspectRatio = null"
			@closing-modal="pendingAspectRatio = null"
		/>
		<div
			v-if="isCommunityCropping"
			class="multi-format-tool__header"
		>
			<div class="multi-format-tool__title">
				<span>Clip your moment</span>
			</div>
			<div class="multi-format-tool__header-right">
				<Button
					v-if="!internalIsMobile"
					class="multi-format-tool__help-button"
					size="medium"
					theme="dark"
					modifier="borderless"
					text="?"
					@click="openHelp"
				/>
			</div>
		</div>
		<div
			v-scroll-container="{
				target: isSidebarsTabbed ? '.js-scroll-wrapper-mobile, .js-scroll-wrapper' : '.js-scroll-wrapper',
				disableScrollShadow: false,
			}"
			class="l-asport-one-layout multi-format-tool__main-content js-scroll-wrapper"
		>
			<div
				v-if="!isSidebarsTabbed && leftSidebarVisible"
				class="l-asport-one-column -left"
			>
				<div class="l-asport-one-panel">
					<MultiFormatAspectRatioSelector
						:value="clipData.aspectRatio"
						:options="aspectRatioOptions"
						:disabled="aspectRatioSelectionDisabled"
						:show-header="true"
						@change="handleAspectRatioChange"
					/>
				</div>
				<div
					v-if="brandingPanelVisible"
					class="l-asport-one-panel -no-v-padding"
				>
					<Accordion
						:open="leftPanelAccordions.branding"
						:title="$I18n.trans('event.multiformat_editor.labels.branding')"
						@toggle="leftPanelAccordions.branding = $event"
					>
						<InputText
							class="multi-format-tool__moment-title"
							:label="$I18n.trans('event.multiformat_editor.labels.moment_title')"
							:value="clipData.title"
							:dark-theme="true"
							:required="true"
							maxlength="120"
							@input="clipData.title = $event"
						/>
						<MultiFormatGraphicSelector
							v-if="hasGraphicOptions"
							class="multi-format-tool__graphic-select"
							:value="selectedGraphicId"
							:options="graphicOptions"
							@change="selectedGraphicId = $event"
						/>
					</Accordion>
				</div>
			</div>
			<div
				v-else-if="isSidebarsTabbed && leftSidebarVisible"
				class="l-asport-one-column -left"
			>
				<Tab
					icon-size="lg"
					class="multi-format-tool__tab-nav desktop-nav"
					:active-tab="activeTab"
					:hide-header="currentUiStatus === UI_STATUS.PREVIEW || tabs.length <= 1"
					:tabs="tabs"
					@tab-change="handleTabChange"
				>
					<template #tab-details-settings>
						<div class="multi-format-tool__sidebar-content js-scroll-wrapper-mobile">
							<div class="l-asport-one-panel">
								<MultiFormatAspectRatioSelector
									:value="clipData.aspectRatio"
									:options="aspectRatioOptions"
									:disabled="aspectRatioSelectionDisabled"
									:show-header="true"
									@change="handleAspectRatioChange"
								/>
							</div>
							<div
								v-if="brandingPanelVisible"
								class="l-asport-one-panel -no-v-padding"
							>
								<Accordion
									:open="leftPanelAccordions.branding"
									:title="$I18n.trans('event.multiformat_editor.labels.branding')"
									@toggle="leftPanelAccordions.branding = $event"
								>
									<InputText
										class="multi-format-tool__moment-title"
										:label="$I18n.trans('event.multiformat_editor.labels.moment_title')"
										:value="clipData.title"
										:dark-theme="true"
										:required="true"
										maxlength="120"
										@input="clipData.title = $event"
									/>
									<MultiFormatGraphicSelector
										v-if="hasGraphicOptions"
										class="multi-format-tool__graphic-select"
										:value="selectedGraphicId"
										:options="graphicOptions"
										@change="selectedGraphicId = $event"
									/>
								</Accordion>
							</div>
						</div>
					</template>
					<template #tab-details-clips>
						<ClipList
							class="l-asport-one-panel -no-padding"
							:hide-context-menu="true"
							:hide-likes="true"
							:hide-rating="true"
							:hide-share="true"
							:hide-filter="true"
							:hide-sort="true"
							default-sort="asc"
							:disabled="currentUiStatus !== UI_STATUS.INITIAL"
							:empty-state-enabled="!isCommunityCropping"
							:hide-empty-list="true"
							@play="handleTagPlay"
						/>
					</template>
				</Tab>
			</div>
			<div
				ref="playerColumn"
				v-adaptive-height="{ target: '.js-multi-format-tool-player-media', disabled: internalIsMobilePortrait && currentUiStatus === UI_STATUS.PREVIEW, updateOnComponentUpdate: false }"
				class="l-asport-one-column -main multi-format-tool__col"
			>
				<div
					class="multi-format-tool__tool-w"
					:class="{ 'l-asport-one-panel': !isMobileViewport }"
				>
					<div
						ref="playerContent"
						class="multi-format-tool__player-content"
						:style="mobilePreviewFrameStyle"
					>
						<Modal
							:modal-name="modalNameDownloadVideo"
							type="dialog"
							:theme="$arenaConfig.isLightTheme ? null : 'dark'"
						>
							<template #title>
								{{ $I18n.trans('commons.type_create', { type: $I18n.trans_choice('commons.video', 1)}) }}
							</template>
							<template #content>
								<div v-if="currentUiStatus === UI_STATUS.EMAIL_NOTIFICATION_REQUESTED">
									<div
										class="multi-format-tool__notification-message"
									>
										<Icon
											icon-name="fireworks"
											size="xl"
										/>
										{{ $I18n.trans('event.multiformat_editor.messages.notification_success') }}
									</div>
								</div>
								<div
									v-else-if="currentUiStatus === UI_STATUS.GENERATING_VIDEO_CROP || currentUiStatus === UI_STATUS.VIDEO_CROP_OVERTIME"
									class="multi-format-tool__in-progress-info"
								>
									<LoadingSpinner
										color="light"
										:theme="$arenaConfig.isLightTheme ? 'dark' : 'light'"
										class="multi-format-tool__loading-spinner"
									/>
									{{ $I18n.trans('one.video_export.progress.create_video') }}
									<div
										v-if="currentUiStatus === UI_STATUS.VIDEO_CROP_OVERTIME"
										class="multi-format-tool__overtime-message"
									>
										{{ $I18n.trans('event.multiformat_editor.messages.processing_overtime') }}
										<InputText
											ref="emailInput"
											:dark-theme="!$arenaConfig.isLightTheme"
											label="E-Mail"
											type="email"
											class="multi-format-tool__overtime-email"
											:value="email"
											@input="(value) => email = value"
										/>
									</div>
									<div
										v-if="emailNotificationError"
										class="multi-format-tool__notification-message"
									>
										<Icon
											icon-name="sad"
											size="xl"
										/>
										{{ $I18n.trans('event.multiformat_editor.messages.notification_error') }}
									</div>
								</div>
								<template v-if="currentUiStatus === UI_STATUS.VIDEO_CROP_AVAILABLE">
									<template v-if="$arenaConfig.enableMomentsFeed">
										{{ $I18n.trans('event.multiformat_editor.messages.video_created_moments') }}
									</template>
									<template v-else>
										{{ $I18n.trans('event.multiformat_editor.messages.video_created') }}
									</template>
									<InputCopy
										v-if="cropVideoDownloadUrl"
										class="multi-format-tool__copy-link"
										:label="$I18n.trans('event.multiformat_editor.labels.copy_link', {days: `14 ${$I18n.trans_choice('commons.day', 14)}`})"
										:value="cropVideoDownloadUrl"
										:no-wrap="true"
										:dark-theme="!$arenaConfig.isLightTheme"
									/>
								</template>
							</template>
							<template #footer>
								<div class="l-button-group">
									<template v-if="currentUiStatus === UI_STATUS.EMAIL_NOTIFICATION_REQUESTED">
										<Button
											:text="$Helpers.ucfirst($I18n.trans('commons.close'))"
											modifier="secondary"
											theme="dark"
											@click="closeDownloadModal"
										/>
									</template>
									<template v-else>
										<Button
											v-if="currentUiStatus !== UI_STATUS.VIDEO_CROP_AVAILABLE"
											:text="$Helpers.ucfirst($I18n.trans('commons.cancel'))"
											modifier="secondary"
											theme="dark"
											@click="closeDownloadModal"
										/>
										<Button
											v-if="currentUiStatus === UI_STATUS.VIDEO_CROP_OVERTIME && !emailNotificationSaved"
											:text="$Helpers.ucfirst($I18n.trans('commons.save'))"
											:disabled="!($refs.emailInput && $refs.emailInput.isValid)"
											theme="dark"
											@click="storeEmailNotification"
										/>
										<Button
											v-if="isShareButtonVisible"
											:text="$Helpers.ucfirst($I18n.trans('commons.type_share', { type: $I18n.trans_choice('commons.video', 1)}))"
											modifier="secondary"
											theme="dark"
											@click="shareVideo"
										/>
										<Button
											v-if="currentUiStatus === UI_STATUS.VIDEO_CROP_AVAILABLE"
											:text="$Helpers.ucfirst($I18n.trans('commons.download'))"
											theme="dark"
											@click="downloadCropVideo"
										/>
									</template>
								</div>
							</template>
						</Modal>

						<div
							ref="playerWrapper"
							class="multi-format-tool__player-w"
							:style="mobilePreviewPlayerStyle"
						>
							<MultiFormatPlayer
								ref="cropperVideo"
								:hls-url="streamUrl"
								:ui-status="currentUiStatus"
								:aspect-ratio="clipData.aspectRatio"
								:graphic-image-url="selectedGraphic?.imageUrl ?? null"
								:show-controls="showControls"
								:tag-time="internalTagTime"
								:internal-is-mobile-portrait="internalIsMobilePortrait"
								:is-mobile-viewport="isMobileViewport"
								:stream-lead-time-seconds="streamLeadTimeSeconds"
								:forced-duration="forcedDuration"
								:is-community-cropping="isCommunityCropping"
								:has-active-keyframe="selectedKeyframeIndex !== null"
								:has-controls-replacement="isMobileViewport && currentUiStatus === UI_STATUS.FINISHED_RECORDING"
								:player-controls-duration-override="currentUiStatus === UI_STATUS.FINISHED_RECORDING ? clipData.duration : null"
								:player-controls-position-override="currentUiStatus === UI_STATUS.FINISHED_RECORDING ? currentClipTime : null"
								:show-player-controls-timeline="currentUiStatus !== UI_STATUS.FINISHED_RECORDING"
								@coordinates-update="coordinatesUpdate"
								@cropstart="handleCropStart"
								@video-ended="handleVideoEnded"
								@duration-update="setMaxDuration"
								@audio-track-change="handleAudioTrackChange"
								@position-update="handlePlayerPositionUpdate"
								@controls-layout-change="updatePlayerAdaptiveHeight"
								@play-state-change="isVideoPlaying = $event"
							>
								<template
									v-if="isMobileViewport"
									#controls
								>
									<MultiFormatKeyframeLane
										class="multi-format-tool__player-keyframe-lane"
										:key-points="clipData.keyPoints"
										:duration="clipData.duration"
										:current-time="currentClipTime"
										:current-x-left="currentCropXLeft"
										:is-playing="isVideoPlaying"
										@change="handleKeyframeLaneChange"
										@duration-change="handleKeyframeLaneDurationChange"
										@seek="handleKeyframeLaneSeek"
										@keyframe-drag-seek="handleKeyframeDragSeek"
										@select="selectedKeyframeIndex = $event"
									/>
								</template>
							</MultiFormatPlayer>
						</div>

						<template v-if="currentUiStatus === UI_STATUS.PREVIEW">
							<div
								ref="previewContainer"
								class="multi-format-tool__preview-container"
								:style="maskStyle"
							/>
							<div
								v-if="selectedGraphic"
								class="multi-format-tool__preview-graphic-container"
								:style="previewGraphicContainerStyle"
							>
								<div class="multi-format-tool__preview-graphic">
									<img
										:src="$Helpers.getScaledImageUrl(selectedGraphic.imageUrl, 1080)"
										class="multi-format-tool__preview-graphic-image"
									>
								</div>
							</div>
							<Button
								v-if="isMobileViewport"
								class="multi-format-tool__preview-close"
								theme="dark"
								modifier="borderless"
								size="small"
								icon="e-remove"
								:title="$Helpers.ucfirst($I18n.trans('commons.cancel'))"
								@click="stopPreview"
							/>
						</template>
					</div>
				</div>
				<div
					v-if="!isMobileViewport && lowerEditorPanelVisible"
					class="multi-format-tool__keyframe-w l-asport-one-panel"
				>
					<MultiFormatKeyframeLane
						v-if="currentUiStatus === UI_STATUS.FINISHED_RECORDING"
						class="multi-format-tool__player-keyframe-lane"
						:key-points="clipData.keyPoints"
						:duration="clipData.duration"
						:current-time="currentClipTime"
						:current-x-left="currentCropXLeft"
						:is-playing="isVideoPlaying"
						@change="handleKeyframeLaneChange"
						@duration-change="handleKeyframeLaneDurationChange"
						@seek="handleKeyframeLaneSeek"
						@keyframe-drag-seek="handleKeyframeDragSeek"
						@select="selectedKeyframeIndex = $event"
					/>
					<div class="multi-format-tool__workflow-row">
						<MultiFormatControls
							class="multi-format-tool__workflow-actions"
							:current-ui-status="currentUiStatus"
							:max-duration-reached-info-visible="maxDurationReachedInfoVisible"
							:remaining-clip-time-secs="remainingClipTimeSecs"
							:remaining-clip-time-secs-leading-zero="remainingClipTimeSecsLeadingZero"
							:preview-countdown="previewCountdown"
							:preview-countdown-leading-zero="previewCountdownLeadingZero"
							:is-community-cropping="isCommunityCropping"
							:selected-graphic-id="selectedGraphicId"
							:graphic-options="graphicOptions"
							:aspect-ratio="clipData.aspectRatio"
							:aspect-ratio-options="aspectRatioOptions"
							:hide-aspect-ratio-selector="true"
							:hide-graphic-settings="true"
							:hide-infos="true"
							:create-button-disabled="!canCreateVideoCrop"
							@setReadyStatus="setReadyStatus"
							@startClipRecording="startClipRecording"
							@stopClipRecording="stopClipRecording"
							@cancelReadyStatus="cancelReadyStatus"
							@previewClip="previewClip"
							@resetClipping="resetClipping"
							@stopPreview="stopPreview"
							@createVideoCrop="createVideoCrop"
							@setSelectedGraphicId="selectedGraphicId = $event"
							@setAspectRatio="handleAspectRatioChange"
						/>
						<MultiFormatStatus
							class="multi-format-tool__workflow-status"
							:current-ui-status="currentUiStatus"
							:max-duration-reached-info-visible="maxDurationReachedInfoVisible"
							:remaining-clip-time-secs="remainingClipTimeSecs"
							:remaining-clip-time-secs-leading-zero="remainingClipTimeSecsLeadingZero"
							:preview-countdown="previewCountdown"
							:preview-countdown-leading-zero="previewCountdownLeadingZero"
						/>
					</div>
				</div>
				<div
					v-if="isMobileViewport && lowerEditorPanelVisible && currentUiStatus !== UI_STATUS.PREVIEW"
					class="multi-format-tool__mobile-actions l-asport-one-panel"
				>
					<MultiFormatControls
						class="multi-format-tool__workflow-actions"
						:current-ui-status="currentUiStatus"
						:max-duration-reached-info-visible="maxDurationReachedInfoVisible"
						:remaining-clip-time-secs="remainingClipTimeSecs"
						:remaining-clip-time-secs-leading-zero="remainingClipTimeSecsLeadingZero"
						:preview-countdown="previewCountdown"
						:preview-countdown-leading-zero="previewCountdownLeadingZero"
						:is-community-cropping="isCommunityCropping"
						:selected-graphic-id="selectedGraphicId"
						:graphic-options="graphicOptions"
						:aspect-ratio="clipData.aspectRatio"
						:aspect-ratio-options="aspectRatioOptions"
						:hide-aspect-ratio-selector="true"
						:hide-graphic-settings="true"
						:hide-infos="true"
						:create-button-disabled="!canCreateVideoCrop"
						button-size="small"
						@setReadyStatus="setReadyStatus"
						@startClipRecording="startClipRecording"
						@stopClipRecording="stopClipRecording"
						@cancelReadyStatus="cancelReadyStatus"
						@previewClip="previewClip"
						@resetClipping="resetClipping"
						@stopPreview="stopPreview"
						@createVideoCrop="createVideoCrop"
						@setSelectedGraphicId="selectedGraphicId = $event"
						@setAspectRatio="handleAspectRatioChange"
					/>
					<MultiFormatStatus
						class="multi-format-tool__workflow-status"
						:current-ui-status="currentUiStatus"
						:max-duration-reached-info-visible="maxDurationReachedInfoVisible"
						:remaining-clip-time-secs="remainingClipTimeSecs"
						:remaining-clip-time-secs-leading-zero="remainingClipTimeSecsLeadingZero"
						:preview-countdown="previewCountdown"
						:preview-countdown-leading-zero="previewCountdownLeadingZero"
					/>
				</div>
			</div>
			<OneSidebarToggle
				v-if="rightSidebarToggleVisible"
				:is-open="rightSidebarOpen"
				@toggle="rightSidebarOpen = !rightSidebarOpen"
			/>
			<div
				v-if="rightSidebarToggleVisible"
				v-show="rightSidebarOpen"
				class="l-asport-one-column -right"
			>
				<div
					class="l-asport-one-panel -no-padding"
				>
					<ClipList
						:hide-context-menu="true"
						:hide-likes="true"
						:hide-rating="true"
						:hide-share="true"
						:hide-filter="true"
						:hide-sort="true"
						default-sort="asc"
						:disabled="currentUiStatus !== UI_STATUS.INITIAL"
						:empty-state-enabled="!isCommunityCropping"
						:hide-empty-list="true"
						@play="handleTagPlay"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import MultiFormatPlayer from '@shared/components/MultiFormatTool/MultiFormatPlayer';
import Button from '@shared/components/Button';
import Icon from '@shared/components/Icon';
import Modal from '@shared/components/Modal';
import ModalConfirm from '@shared/components/ModalConfirm';
import Accordion from '@components/accordion/Accordion';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import InputText from '@shared/components/Form/InputText';
import InputCopy from '@shared/components/Form/InputCopy';
import ClipList from '@videotools/js/components/clip-list/ClipList';
import MultiFormatAspectRatioSelector from '@shared/components/MultiFormatTool/MultiFormatAspectRatioSelector.vue';
import MultiFormatGraphicSelector from '@shared/components/MultiFormatTool/MultiFormatGraphicSelector.vue';
import MultiFormatControls from '@shared/components/MultiFormatTool/MultiFormatControls';
import MultiFormatStatus from '@shared/components/MultiFormatTool/MultiFormatStatus.vue';
import MultiFormatKeyframeLane from '@shared/components/MultiFormatTool/MultiFormatKeyframeLane.vue';
import ModalKeyboardShortcuts from '@shared/components/ModalKeyboardShortcuts.vue';
import OneSidebarToggle from '@videotools/js/one/components/SidebarToggle.vue';
import Tab from '@components/tab';
import ws from '@shared/js/websocket/websocket-client.js';
import { WS_EVENT } from '@shared/js/websocket/constants.js';
import { getShortLinkForMoment } from '@shared/utils/getShortLink.js';
import { httpClient } from '@shared/js/api/httpClient';
import { leadingZero } from '@shared/utils/formatNumber.js';
import VScrollContainer from '@shared/directives/v-scroll-container';
import AdaptiveHeight from '@shared/directives/v-adaptive-height';
import GraphicsMixin from '@shared/js/mixins/GraphicsMixin';
import { GraphicAspectRatio } from '@shared/enums/GraphicAspectRatio.js';
import { getGraphicAspectRatioOptions, getGraphicAspectRatioValue } from '@shared/utils/graphicAspectRatio.js';
import { VideoCropStatus } from '@shared/enums/VideoCropStatus.js';
import { EventVODStatus } from '@shared/enums/EventVODStatus.js';
import { mapGetters } from 'vuex';
import { simplifyCropPath } from './cropPathSimplifier.js';

const CLIP_SAMPLE_INTERVAL_MS = 500;
const CROP_VIDEO_PROCESSING_OVERTIME = 60000; // 1 minute
const CLIP_MAX_DURATION_MS = 30000;
const CROP_PATH_MAX_X_ERROR_PX = 15;
const CROP_EDGE_SNAP_PX = 3;

export const UI_STATUS = {
	INITIAL:                      'initial',
	READY:                        'ready',
	RECORDING:                    'recording',
	FINISHED_RECORDING:           'finished-recording',
	PREVIEW:                      'preview',
	GENERATING_VIDEO_CROP:        'generating-video-crop',
	VIDEO_CROP_AVAILABLE:         'video-crop-available',
	VIDEO_CROP_FAILURE:           'video-crop-failure',
	VIDEO_CROP_OVERTIME:          'video-crop-overtime',
	EMAIL_NOTIFICATION_REQUESTED: 'email-notification-requested',
};

export const KEYBOARD_SHORTCUTS = [
	{
		key:         'Enter',
		description: $I18n.trans('one.keyboard_shortcuts.keys.capture:enter'),
		shiftKey:    false,
	},
	{
		key:         'Escape',
		description: $I18n.trans('one.keyboard_shortcuts.keys.capture:escape'),
		shiftKey:    false,
	},
	{
		key:         ' ',
		description: $I18n.trans('one.keyboard_shortcuts.keys.hotkey:toggle-play'),
		shiftKey:    false,
	},
	{
		key:         'ArrowLeft',
		description: $I18n.trans('one.keyboard_shortcuts.keys.hotkey:seek-backwards-one-sec'),
		shiftKey:    false,
	},
	{
		key:         'ArrowRight',
		description: $I18n.trans('one.keyboard_shortcuts.keys.hotkey:seek-forwards-one-sec'),
		shiftKey:    false,
	},
];

const getMobileQuery = () => {
	return window.matchMedia('(max-width: 1024px)').matches && window.matchMedia('(pointer: coarse)').matches;
};
const getPortraitQuery = () => {
	return window.matchMedia('(orientation: portrait)').matches;
};
const getLandscapeQuery = () => {
	return window.matchMedia('(orientation: landscape)').matches;
};

export default {
	name:       'MultiFormatTool',
	components: { MultiFormatPlayer, Button, Modal, ModalConfirm, Accordion, LoadingSpinner, InputText, Icon, InputCopy, ClipList, MultiFormatAspectRatioSelector, MultiFormatGraphicSelector, MultiFormatControls, MultiFormatStatus, MultiFormatKeyframeLane, Tab, ModalKeyboardShortcuts, OneSidebarToggle },
	directives: {
		scrollContainer: VScrollContainer,
		adaptiveHeight:  AdaptiveHeight,
	},
	mixins: [GraphicsMixin],
	props:  {
		eventId: {
			type:     Number,
			required: true,
		},
		tagTime: {
			type:    Object,
			default: null,
		},
		graphicSets: {
			type:    Array,
			default: () => [],
		},
		eventLabels: {
			type:    Array,
			default: () => [],
		},
		eventTitle: {
			type:    String,
			default: '',
		},
		defaultGraphicSet: {
			type:    Object,
			default: null,
		},
		isCommunityCropping: {
			type:    Boolean,
			default: false,
		},
		uiStatus: {
			type:    String,
			default: null,
		},
	},
	data() {
		return {
			cropper:               null,
			clipUrl:               null,
			email:                 null,
			elapsedClipTime:       0,
			previewCountdown:      0,
			dataGraphicSets:       this.graphicSets,
			dataDefaultGraphicSet: this.defaultGraphicSet,
			clipData:              {
				keyPoints:                  [],
				startTime:                  0,
				duration:                   0,
				title:                      this.eventTitle,
				aspectRatio:                GraphicAspectRatio.AR_9_16,
				audioTrackSelectionFilters: 'all',
			},
			maxClipDuration:          CLIP_MAX_DURATION_MS,
			clipInterval:             null,
			previewAnimationFrame:    null,
			previewCountdownInterval: null,
			cropOvertimeInterval:     null,
			cropTaskWsSubscription:   null,
			cropVideoId:              null,
			cropVideoDownloadUrl:     null,
			cropVideoStatus:          null,
			cropVideoStart:           null,
			selectedGraphicId:        null,
			cropPosition:             {
				x:     0,
				width: 0,
			},
			maxDurationReachedInfoVisible:  false,
			emailNotificationSaved:         false,
			emailNotificationError:         false,
			modalNameDownloadVideo:         'download-video',
			modalNameAspectRatioChange:     'multiformat-aspect-ratio-change',
			internalUiStatus:               UI_STATUS.INITIAL,
			UI_STATUS,
			internalIsMobile:               getMobileQuery(),
			internalIsPortrait:             getPortraitQuery(),
			internalIsLandscape:            getLandscapeQuery(),
			videoReadyForSharing:           false,
			videoBlob:                      null,
			activeTab:                      'settings',
			currentAudioTrack:              null,
			pendingAspectRatio:             null,
			internalTagTime:                this.tagTime,
			modalNameKeyboardShortcuts:     'ModalKeyboardShortcuts',
			currentClipTime:                0,
			currentCropXLeft:               0,
			selectedKeyframeIndex:          null,
			isApplyingCropPosition:         false,
			isVideoPlaying:                 false,
			keyframePlaybackAnimationFrame: null,
			pendingGraphicId:               null,
			rightSidebarOpen:               true,
			leftPanelAccordions:            {
				branding: true,
			},
		};
	},
	computed: {
		...mapGetters({
			event:                 'event/event',
			streamLeadTimeSeconds: 'event/streamLeadTimeSeconds',
			streamLagTimeSeconds:  'event/streamLagTimeSeconds',
			streamUrl:             'event/streamURL',
			hasTags:               'event/hasTags',
			isNarrowTaggingTool:   'device/isNarrowTaggingTool',
			isMobileViewport:      'device/isMobileViewport',
		}),
		forcedDuration() {
			if (this.event && !this.event.vodOnly && this.event.vodStatus !== EventVODStatus.AVAILABLE && Number.isFinite(this.event.durationSeconds)) {
				return this.event.durationSeconds - this.streamLagTimeSeconds;
			}
			return undefined;
		},
		tabs() {
			const tabs = [
				{
					key:  'settings',
					icon: 'settings-gear',
				},
			];

			if (this.hasTags) {
				tabs.push({
					key:  'clips',
					icon: 'video-playlist',
				});
			}

			return tabs;
		},
		previewCountdownLeadingZero() {
			return leadingZero(this.previewCountdown);
		},
		remainingClipTimeSecsLeadingZero() {
			return leadingZero(this.remainingClipTimeSecs);
		},
		currentUiStatus() {
			// Use the prop value if provided, otherwise use internal state
			return this.uiStatus || this.internalUiStatus;
		},
		showControls() {
			return ![UI_STATUS.READY, UI_STATUS.PREVIEW, UI_STATUS.RECORDING].includes(this.currentUiStatus);
		},
		internalIsMobilePortrait() {
			return this.internalIsMobile && this.internalIsPortrait;
		},
		internalIsMobileLandscape() {
			return this.internalIsMobile && this.internalIsLandscape;
		},
		remainingClipTimeSecs() {
			return Math.max(0, Math.ceil((this.maxClipDuration - this.elapsedClipTime) / 1000));
		},
		controlsWrapperPositionStyle() {
			return {
				width:     `${this.cropPosition.width}px`,
				transform: `translateX(${this.cropPosition.x}px)`,
			};
		},
		// Keep preview math in one coordinate system, even if Cropper briefly reports a zero-sized box.
		previewCropWidth() {
			if (this.cropPosition.width > 8) {
				return this.cropPosition.width;
			}

			const cropContainerSize = this.$refs?.cropperVideo?.getCropContainerSize?.() ?? {};
			const frameHeight = this.$refs?.playerContent?.clientHeight || cropContainerSize.height || 0;
			const frameWidth = this.$refs?.playerContent?.clientWidth || cropContainerSize.width || 0;
			const widthFromAspectRatio = frameHeight * this.aspectRatioValue;

			if (widthFromAspectRatio <= 0) {
				return this.cropPosition.width;
			}

			return frameWidth > 0 ? Math.min(frameWidth, widthFromAspectRatio) : widthFromAspectRatio;
		},
		previewGraphicContainerStyle() {
			return {
				width: `${this.previewCropWidth + 2}px`,
			};
		},
		maskStyle() {
			return {
				maskImage: `linear-gradient(to right,
				black 0%,
			black calc(50% - ${Math.floor(this.previewCropWidth / 2)}px),
			transparent calc(50% - ${this.previewCropWidth / 2}px),
			transparent calc(50% + ${this.previewCropWidth / 2}px),
			black calc(50% + ${Math.floor(this.previewCropWidth / 2)}px),
					black 100%)`,
			};
		},
		aspectRatioValue() {
			return getGraphicAspectRatioValue(this.clipData.aspectRatio);
		},
		mobilePreviewFrameStyle() {
			if (!this.isMobileViewport || this.currentUiStatus !== UI_STATUS.PREVIEW) {
				return null;
			}

			return {
				aspectRatio: `${this.aspectRatioValue}`,
				width:       `min(100vw, calc(100vh * ${this.aspectRatioValue}))`,
				height:      `min(100vh, calc(100vw / ${this.aspectRatioValue}))`,
				maxWidth:    'none',
				maxHeight:   'none',
				flexShrink:  '0',
			};
		},
		mobilePreviewPlayerStyle() {
			if (!this.isMobileViewport || this.currentUiStatus !== UI_STATUS.PREVIEW) {
				return null;
			}

			return {
				width: `calc(100% * ${16 / 9 / this.aspectRatioValue})`,
			};
		},
		selectedGraphic() {
			return this.graphicOptions.find(graphic => graphic.value === this.selectedGraphicId);
		},
		aspectRatioOptions() {
			return getGraphicAspectRatioOptions().map(aspectRatio => ({
				label: aspectRatio,
				value: aspectRatio,
			}));
		},
		aspectRatioSelectionDisabled() {
			return [
				UI_STATUS.READY,
				UI_STATUS.RECORDING,
				UI_STATUS.PREVIEW,
				UI_STATUS.GENERATING_VIDEO_CROP,
				UI_STATUS.VIDEO_CROP_OVERTIME,
				UI_STATUS.EMAIL_NOTIFICATION_REQUESTED,
			].includes(this.currentUiStatus);
		},
		canCreateVideoCrop() {
			return this.currentUiStatus === UI_STATUS.FINISHED_RECORDING && this.clipData.title.trim().length > 0;
		},
		lowerEditorPanelVisible() {
			return [
				UI_STATUS.INITIAL,
				UI_STATUS.READY,
				UI_STATUS.RECORDING,
				UI_STATUS.PREVIEW,
				UI_STATUS.FINISHED_RECORDING,
			].includes(this.currentUiStatus) || this.maxDurationReachedInfoVisible;
		},
		isSidebarsTabbed() {
			return this.isNarrowTaggingTool;
		},
		leftSidebarVisible() {
			return !this.isMobileViewport || this.currentUiStatus !== UI_STATUS.PREVIEW;
		},
		rightSidebarToggleVisible() {
			return !this.isSidebarsTabbed && !(this.isCommunityCropping && !this.hasTags);
		},
		hasGraphicOptions() {
			return this.graphicOptions.some(graphicOption => graphicOption.value !== null);
		},
		brandingPanelVisible() {
			return !this.isCommunityCropping;
		},
		graphicOptions() {
			let sets = this.dataGraphicSets;
			const options = [];

			// Add an option for no logo on non-community crops
			if (!this.dataDefaultGraphicSet && !this.isCommunityCropping) {
				options.push({ label: $I18n.trans('event.multiformat_editor.labels.no_logo'), value: null });
			}

			if (this.isCommunityCropping && this.dataDefaultGraphicSet) {
				sets = [this.dataDefaultGraphicSet];
			}

			sets.forEach(set => {
				const validGraphics = set.graphics.filter(graphic => [this.clipData.aspectRatio].includes(graphic.aspectRatio));
				validGraphics.forEach(graphic => {
					const data = {
						label:       graphic.name || set.name,
						value:       graphic.id,
						imageUrl:    graphic.imageUrl,
						aspectRatio: graphic.aspectRatio,
						setId:       set.id,
					};

					if (set.isAdButlerGraphic) {
						data.adButlerConfig = {
							zoneId:   set.adButlerZoneId,
							keywords: set.adButlerSendLabels ? this.eventLabels : [],
							format:   graphic.adButlerFormat,
						};
					}

					options.push(data);
				});
			});

			return options;
		},
		isFileSharingSupported() {
			return navigator.share && navigator.canShare;
		},
		isShareButtonVisible() {
			return this.currentUiStatus === UI_STATUS.VIDEO_CROP_AVAILABLE &&
				this.isFileSharingSupported &&
				this.isCommunityCropping &&
				this.videoReadyForSharing;
		},
	},
	watch: {
		cropVideoStatus(value) {
			if (value === VideoCropStatus.CREATED) {
				this.internalUiStatus = UI_STATUS.GENERATING_VIDEO_CROP;
				this.eventBus.$emit(`modal:${this.modalNameDownloadVideo}`);
			}
			if (value === VideoCropStatus.BUILDING) {
				this.internalUiStatus = UI_STATUS.GENERATING_VIDEO_CROP;
			}
			if ([VideoCropStatus.AVAILABLE, VideoCropStatus.PROCESSING].includes(value)) {
				this.internalUiStatus = UI_STATUS.VIDEO_CROP_AVAILABLE;
			}
			if (value === VideoCropStatus.FAILED) {
				this.internalUiStatus = UI_STATUS.VIDEO_CROP_FAILURE;
				this.eventBus.$emit(`modalClose:${this.modalNameDownloadVideo}`);
				this.$toast.error(this.$I18n.trans('commons.something_went_wrong'));
			}

			if ([VideoCropStatus.AVAILABLE, VideoCropStatus.PROCESSING, VideoCropStatus.FAILED].includes(value)) {
				if (this.cropOvertimeInterval) {
					clearInterval(this.cropOvertimeInterval);
				}
			}
		},
		isVideoPlaying(isPlaying) {
			if (isPlaying && this.currentUiStatus === UI_STATUS.FINISHED_RECORDING) {
				this.startKeyframePlaybackAnimation();
				return;
			}

			this.stopKeyframePlaybackAnimation();
		},
		currentUiStatus(value) {
			this.$nextTick(this.updatePlayerAdaptiveHeight);

			if (value === UI_STATUS.FINISHED_RECORDING && this.isVideoPlaying) {
				this.startKeyframePlaybackAnimation();
				return;
			}

			this.stopKeyframePlaybackAnimation();
		},
		isSidebarsTabbed() {
			this.$nextTick(() => {
				this.activeTab = this.tabs[0].key;
			});
		},
	},
	mounted() {
		this.handleKeybindings();
		this.handleOrientationChange();
		this.setDefaultGraphic();
	},
	beforeDestroy() {
		// Remove event listeners to prevent memory leaks
		document.removeEventListener('keydown', this.handleKeydown);
		this.stopKeyframePlaybackAnimation();
		window.matchMedia('(orientation: portrait)').removeEventListener('change', this.handleOrientationChangeEvent);
		window.matchMedia('(max-width: 1024px)').removeEventListener('change', this.handleOrientationChangeEvent);
	},
	methods: {
		handleKeybindings() {
			document.addEventListener('keydown', this.handleKeydown);
		},
		setDefaultGraphic() {
			this.selectedGraphicId = this.graphicOptions[0]?.value || null;
		},
		setGraphicForAspectRatio(preferredGraphicId = null) {
			const preferredGraphic = this.graphicOptions.find(graphic => graphic.value === preferredGraphicId);
			this.selectedGraphicId = preferredGraphic?.value ?? this.graphicOptions[0]?.value ?? null;
		},
		getSelectedGraphicIdForAspectRatio(aspectRatio) {
			if (!this.selectedGraphicId) {
				return null;
			}

			const sets = this.isCommunityCropping && this.dataDefaultGraphicSet
				? [this.dataDefaultGraphicSet]
				: this.dataGraphicSets;
			const selectedSet = sets.find(set => set.graphics.some(graphic => graphic.id === this.selectedGraphicId));

			// graphicOptions is already filtered by the current aspect ratio, so use the raw set data before switching.
			return selectedSet?.graphics.find(graphic => graphic.aspectRatio === aspectRatio)?.id ?? null;
		},
		handleKeydown(event) {
			if (this.isTextInputEvent(event)) {
				return;
			}

			if (event.key === ' ' && this.currentUiStatus === UI_STATUS.FINISHED_RECORDING) {
				event.preventDefault();
				this.toggleKeyframeLanePlayback();
			}

			if (event.key === 'Enter') {
				if (this.currentUiStatus === UI_STATUS.INITIAL) {
					this.handleInitialRecordingAction();
				} else if (this.currentUiStatus === UI_STATUS.READY) {
					this.startClipRecording();
				} else if (this.currentUiStatus === UI_STATUS.RECORDING) {
					this.stopClipRecording();
				}
			}

			if (event.key === 'ArrowLeft') {
				if (this.currentUiStatus === UI_STATUS.INITIAL) {
					this.$refs.cropperVideo.seek(-1);
				}
			}

			if (event.key === 'ArrowRight') {
				if (this.currentUiStatus === UI_STATUS.INITIAL) {
					this.$refs.cropperVideo.seek(1);
				}
			}

			if (event.key === 'Escape') {
				if ([UI_STATUS.RECORDING, UI_STATUS.READY].includes(this.currentUiStatus)) {
					this.cancelReadyStatus();
				}
			}
		},
		isTextInputEvent(event) {
			const target = event.target;
			if (!(target instanceof HTMLElement)) {
				return false;
			}

			return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable || Boolean(target.closest('[contenteditable="true"]'));
		},
		handleOrientationChange() {
			window.matchMedia('(orientation: portrait)').addEventListener('change', this.handleOrientationChangeEvent);
			window.matchMedia('(max-width: 1024px)').addEventListener('change', this.handleOrientationChangeEvent);
		},
		handleOrientationChangeEvent(_event) {
			this.internalIsMobile = getMobileQuery();
			this.internalIsPortrait = getPortraitQuery();
			this.internalIsLandscape = getLandscapeQuery();
		},
		coordinatesUpdate(data) {
			window.requestAnimationFrame(() => {
				const cropContainerWidth = this.$refs?.cropperVideo?.getCropContainerSize?.().width ?? 0;
				if (data.width <= 8 || cropContainerWidth <= 0) {
					this.isApplyingCropPosition = false;
					return;
				}

				this.cropPosition.width = data.width + 2;
				this.cropPosition.x = this.getSnappedCropLeft(data.left, data.width, cropContainerWidth) + 2;
				this.currentCropXLeft = this.getNormalizedCropXLeft(data.left, data.width, cropContainerWidth);
				if (!this.isApplyingCropPosition) {
					this.updateSelectedKeyframeXLeft();
				}
				this.isApplyingCropPosition = false;
			});
		},
		handleCropStart() {
			if (this.currentUiStatus === UI_STATUS.READY) {
				this.startClipRecording();
			}
		},
		handleAudioTrackChange(audioTrack) {
			this.currentAudioTrack = audioTrack;
		},
		pauseVideo() {
			this.$refs.cropperVideo.pause();
		},
		playVideo() {
			this.$refs.cropperVideo.play();
		},
		toggleVideoPlay() {
			if (this.$refs.cropperVideo.isPlaying) {
				this.pauseVideo();
			} else {
				this.playVideo();
			}
		},
		async toggleKeyframeLanePlayback() {
			if (this.isVideoPlaying) {
				this.pauseVideo();
				return;
			}

			if (this.currentClipTime >= this.clipData.duration) {
				await this.seekRecordedClip(0);
			}

			this.playVideo();
		},
		startKeyframePlaybackAnimation() {
			if (this.keyframePlaybackAnimationFrame) {
				return;
			}

			const update = () => {
				if (this.currentUiStatus !== UI_STATUS.FINISHED_RECORDING || !this.isVideoPlaying) {
					this.keyframePlaybackAnimationFrame = null;
					return;
				}

				const currentTime = this.$refs.cropperVideo.getCurrentTime();
				const clipTime = Math.max(0, Math.min(this.clipData.duration, currentTime - this.clipData.startTime));

				this.currentClipTime = clipTime;
				this.applyCropPositionForTime(clipTime);

				if (clipTime >= this.clipData.duration) {
					this.pauseVideo();
					this.seekRecordedClip(this.clipData.duration);
					this.keyframePlaybackAnimationFrame = null;
					return;
				}

				this.keyframePlaybackAnimationFrame = requestAnimationFrame(update);
			};

			this.keyframePlaybackAnimationFrame = requestAnimationFrame(update);
		},
		stopKeyframePlaybackAnimation() {
			cancelAnimationFrame(this.keyframePlaybackAnimationFrame);
			this.keyframePlaybackAnimationFrame = null;
		},
		async resetClipping(aspectRatio = this.clipData.aspectRatio || GraphicAspectRatio.AR_9_16, preferredGraphicId = null) {
			await this.$refs.cropperVideo.setVideoTime(this.clipData.startTime ?? 0, this.clipData.startTime !== 0);
			this.clipData = {
				keyPoints:                  [],
				startTime:                  0,
				duration:                   0,
				title:                      this.eventTitle,
				aspectRatio,
				audioTrackSelectionFilters: 'all',
			};
			this.elapsedClipTime = 0;
			this.internalUiStatus = UI_STATUS.INITIAL;
			this.emailNotificationSaved = false;
			this.emailNotificationError = false;
			this.email = null;
			this.cropVideoId = null;
			this.cropVideoDownloadUrl = null;
			this.cropVideoStatus = null;
			this.pauseVideo();
			this.currentClipTime = 0;
			this.selectedKeyframeIndex = null;
			this.$nextTick(() => this.setGraphicForAspectRatio(preferredGraphicId));
		},
		handleAspectRatioChange(aspectRatio) {
			if (aspectRatio === this.clipData.aspectRatio) {
				return;
			}

			const preferredGraphicId = this.getSelectedGraphicIdForAspectRatio(aspectRatio);
			const hasCropWork = this.currentUiStatus !== UI_STATUS.INITIAL || this.clipData.keyPoints.length > 0 || this.clipData.duration > 0;
			if (hasCropWork) {
				this.pendingAspectRatio = aspectRatio;
				this.pendingGraphicId = preferredGraphicId;
				this.eventBus.$emit(`modal:${this.modalNameAspectRatioChange}`);
				return;
			}

			this.setAspectRatio(aspectRatio, preferredGraphicId);
		},
		confirmAspectRatioChange() {
			const aspectRatio = this.pendingAspectRatio;
			const preferredGraphicId = this.pendingGraphicId;
			this.pendingAspectRatio = null;
			this.pendingGraphicId = null;
			if (!aspectRatio) {
				return;
			}

			this.resetClipping(aspectRatio, preferredGraphicId);
		},
		setAspectRatio(aspectRatio, preferredGraphicId = null) {
			this.clipData = {
				...this.clipData,
				aspectRatio,
			};
			this.selectedKeyframeIndex = null;
			this.$nextTick(() => this.setGraphicForAspectRatio(preferredGraphicId));
		},
		setReadyStatus() {
			this.internalUiStatus = UI_STATUS.READY;
		},
		handleInitialRecordingAction() {
			if (this.clipData.aspectRatio === GraphicAspectRatio.AR_16_9) {
				this.startClipRecording();
				return;
			}

			this.setReadyStatus();
		},
		cancelReadyStatus() {
			this.internalUiStatus = UI_STATUS.INITIAL;
			this.pauseVideo();
			this.resetClipping();

			if (this.clipInterval) {
				clearInterval(this.clipInterval);
				this.elapsedClipTime = 0;
			}
		},
		handleVideoEnded() {
			if (this.currentUiStatus === UI_STATUS.RECORDING) {
				this.stopClipRecording();
			}
		},
		startClipRecording() {
			this.internalUiStatus = UI_STATUS.RECORDING;

			// Set player to play
			this.playVideo();

			this.clipData.startTime = this.$refs?.cropperVideo.position;
			this.currentClipTime = 0;
			this.selectedKeyframeIndex = null;

			// Save initial position
			this.saveClipData();

			this.clipInterval = setInterval(() => {
				this.elapsedClipTime = this.getCurrentClipElapsedMs();
				this.saveClipData();
				if (this.elapsedClipTime >= this.maxClipDuration) {
					this.showMaxDurationReachedInfo();
					this.stopClipRecording();
				}
			}, CLIP_SAMPLE_INTERVAL_MS);
		},
		async stopClipRecording() {
			this.internalUiStatus = UI_STATUS.FINISHED_RECORDING;
			this.elapsedClipTime = this.getCurrentClipElapsedMs();
			this.saveClipData();
			this.clipData.duration = Math.max(1, this.getCurrentClipElapsedSeconds());
			this.optimizeClipKeyPoints();
			this.pauseVideo();
			if (this.clipInterval) {
				clearInterval(this.clipInterval);
				this.elapsedClipTime = 0;
			}
			await this.seekRecordedClip(0);
		},
		async previewClip() {
			this.internalUiStatus = UI_STATUS.PREVIEW;
			this.activeTab = 'settings';

			// Set the imageUrl in the dataGraphicSet to the respective graphic
			if (this.selectedGraphic?.adButlerConfig) {
				const graphic = this.dataDefaultGraphicSet?.graphics.find(graphic => graphic.id === this.selectedGraphicId) ??
						this.dataGraphicSets.find(set => set.id === this.selectedGraphic.setId)?.graphics.find(graphic => graphic.id === this.selectedGraphicId);
				if (graphic) {
					graphic.imageUrl = await this.getAdServerGraphicImage(
						this.selectedGraphicId,
						this.selectedGraphic.adButlerConfig.zoneId,
						this.selectedGraphic.adButlerConfig.format,
						this.selectedGraphic.adButlerConfig.keywords,
					);
				}
			}

			await this.$refs.cropperVideo.setVideoTime(this.internalTagTime ? this.clipData.startTime - this.internalTagTime.start : this.clipData.startTime, true);
			await this.$refs.cropperVideo.play();
			this.$refs.playerWrapper.style.transition = '';
			this.updatePreviewCropPosition(0);

			this.previewCountdown = Math.ceil(this.clipData.duration);

			this.previewCountdownInterval = setInterval(() => {
				this.previewCountdown = Math.max(0, this.previewCountdown - 1);
			}, 1000);
			this.startPreviewAnimation();
		},
		stopPreview() {
			this.internalUiStatus = UI_STATUS.FINISHED_RECORDING;
			this.$refs.cropperVideo.pause();
			this.$refs.playerWrapper.style.transition = '';
			this.$refs.playerWrapper.style.transform = 'translateX(0)';
			cancelAnimationFrame(this.previewAnimationFrame);
			clearInterval(this.previewCountdownInterval);
		},
		async handleTagPlay(clip) {
			this.internalTagTime = null;
			const start = Math.max(0, clip.timestamp - clip.secondsBefore);
			await this.$refs.cropperVideo.setVideoTime(start);
			this.$refs.cropperVideo.play();
		},
		async seekRecordedClip(time) {
			this.currentClipTime = Math.max(0, Math.min(this.clipData.duration, time));
			await this.$refs.cropperVideo.setVideoTime(this.clipData.startTime + this.currentClipTime, true);
			this.applyCropPositionForTime(this.currentClipTime);
		},
		async handleKeyframeLaneSeek(time) {
			this.pauseVideo();
			await this.seekRecordedClip(time);
		},
		handleKeyframeDragSeek(time) {
			if (this.isVideoPlaying) {
				this.pauseVideo();
			}

			this.currentClipTime = Math.max(0, Math.min(this.clipData.duration, time));
			this.$refs.cropperVideo.setVideoTime(this.clipData.startTime + this.currentClipTime, true);
			this.applyCropPositionForTime(this.currentClipTime);
		},
		handlePlayerPositionUpdate(position) {
			if (![UI_STATUS.FINISHED_RECORDING, UI_STATUS.PREVIEW].includes(this.currentUiStatus)) {
				return;
			}

			this.currentClipTime = Math.max(0, Math.min(this.clipData.duration, position - this.clipData.startTime));

			if (this.currentUiStatus === UI_STATUS.FINISHED_RECORDING) {
				this.applyCropPositionForTime(this.currentClipTime);
				if (this.isVideoPlaying && position >= this.clipData.startTime + this.clipData.duration) {
					this.pauseVideo();
					this.seekRecordedClip(this.clipData.duration);
				}
			}
		},
		updatePlayerAdaptiveHeight() {
			this.$refs.playerColumn?._updateTargetMaxHeight?.();
		},
		handleKeyframeLaneChange(keyPoints) {
			this.clipData.keyPoints = keyPoints;
			this.applyCropPositionForTime(this.currentClipTime);
		},
		handleKeyframeLaneDurationChange(duration) {
			this.clipData.duration = duration;
			this.currentClipTime = Math.min(this.currentClipTime, duration);
		},
		updateSelectedKeyframeXLeft() {
			if (this.currentUiStatus !== UI_STATUS.FINISHED_RECORDING || this.selectedKeyframeIndex === null) {
				return;
			}

			this.clipData.keyPoints = this.clipData.keyPoints.map((keyPoint, keyPointIndex) => {
				if (keyPointIndex !== this.selectedKeyframeIndex) {
					return keyPoint;
				}

				return {
					...keyPoint,
					xLeft: this.currentCropXLeft,
				};
			});
		},
		applyCropPositionForTime(time) {
			const cropperRef = this.$refs?.cropperVideo;
			const cropBoxData = cropperRef?.cropper?.getCropBoxData();
			const cropContainerWidth = cropperRef?.getCropContainerSize().width ?? 0;
			const xLeft = this.getInterpolatedXLeftForTime(time);

			if (!cropBoxData || cropContainerWidth <= 0) {
				return;
			}

			cropperRef.cropper.setCropBoxData({
				...cropBoxData,
				left: xLeft * cropContainerWidth,
			});
			this.isApplyingCropPosition = true;
			cropperRef.syncCropBoxData();
		},
		getSnappedCropLeft(left, width, containerWidth) {
			const maxLeft = Math.max(0, containerWidth - width);

			if (left <= CROP_EDGE_SNAP_PX) {
				return 0;
			}

			if (maxLeft - left <= CROP_EDGE_SNAP_PX) {
				return maxLeft;
			}

			return Math.max(0, Math.min(left, maxLeft));
		},
		getNormalizedCropXLeft(left, width, containerWidth) {
			if (containerWidth <= 0) {
				return 0;
			}

			return this.getSnappedCropLeft(left, width, containerWidth) / containerWidth;
		},
		getInterpolatedXLeftForTime(time) {
			const keyPoints = this.clipData.keyPoints ?? [];
			if (!keyPoints.length) {
				return this.currentCropXLeft;
			}

			const sortedKeyPoints = [...keyPoints].sort((firstPoint, secondPoint) => firstPoint.t - secondPoint.t);
			const nextKeyPoint = sortedKeyPoints.find(keyPoint => keyPoint.t >= time);
			const previousKeyPoint = [...sortedKeyPoints].reverse().find(keyPoint => keyPoint.t <= time);

			if (!previousKeyPoint) {
				return nextKeyPoint.xLeft;
			}
			if (!nextKeyPoint) {
				return previousKeyPoint.xLeft;
			}
			if (nextKeyPoint.t === previousKeyPoint.t) {
				return previousKeyPoint.xLeft;
			}

			const progress = (time - previousKeyPoint.t) / (nextKeyPoint.t - previousKeyPoint.t);
			return previousKeyPoint.xLeft + ((nextKeyPoint.xLeft - previousKeyPoint.xLeft) * progress);
		},
		saveClipData() {
			const cropperRef = this.$refs?.cropperVideo;
			const cropBoxData = cropperRef?.cropper?.getCropBoxData();
			const elapsedSeconds = this.getCurrentClipElapsedSeconds();
			this.clipData.keyPoints.push({
				t:       elapsedSeconds,
				yTop:    (cropBoxData?.top ?? 0) / (cropperRef?.getCropContainerSize().height ?? 0), // Percentage top offset
				xLeft:   this.getNormalizedCropXLeft(cropBoxData?.left ?? 0, cropBoxData?.width ?? 0, cropperRef?.getCropContainerSize().width ?? 0), // Percentage left offset
				xWidth:  (cropBoxData?.width ?? 0) / (cropperRef?.getCropContainerSize().width ?? 0), // Percentage width
				yHeight: (cropBoxData?.height ?? 0) / (cropperRef?.getCropContainerSize().height ?? 0), // Percentage height
			});
		},
		getCurrentClipElapsedSeconds() {
			const cropperRef = this.$refs?.cropperVideo;
			const elapsedSeconds = (cropperRef?.position ?? this.clipData.startTime) - this.clipData.startTime;

			return Math.max(0, Math.min(this.maxClipDuration / 1000, elapsedSeconds));
		},
		getCurrentClipElapsedMs() {
			return Math.round(this.getCurrentClipElapsedSeconds() * 1000);
		},
		optimizeClipKeyPoints() {
			const cropperRef = this.$refs?.cropperVideo;
			const cropContainerWidth = cropperRef?.getCropContainerSize().width ?? 0;

			this.clipData.keyPoints = simplifyCropPath(this.clipData.keyPoints, cropContainerWidth, CROP_PATH_MAX_X_ERROR_PX);
		},
		startPreviewAnimation() {
			cancelAnimationFrame(this.previewAnimationFrame);

			const update = () => {
				if (this.currentUiStatus !== UI_STATUS.PREVIEW) {
					return;
				}

				const currentTime = this.$refs.cropperVideo.getCurrentTime();
				const clipTime = Math.max(0, Math.min(this.clipData.duration, currentTime - this.clipData.startTime));
				this.updatePreviewCropPosition(clipTime);

				if (clipTime >= this.clipData.duration) {
					this.stopPreview();
					return;
				}

				this.previewAnimationFrame = requestAnimationFrame(update);
			};

			this.previewAnimationFrame = requestAnimationFrame(update);
		},
		updatePreviewCropPosition(clipTime) {
			const xLeft = Math.max(0, this.getInterpolatedXLeftForTime(clipTime));
			this.$refs.playerWrapper.style.transition = '';

			if (!this.internalIsMobilePortrait) {
				this.$refs.playerWrapper.style.transform = `translateX(calc(50% - ${this.previewCropWidth / 2}px - ${xLeft * 100}%))`;
				return;
			}

			const videoWidth = this.$refs.playerWrapper?.clientWidth ?? 0;
			const frameWidth = this.$refs.playerContent?.clientWidth ?? 0;
			const maxXLeft = videoWidth > 0 ? Math.max(0, 1 - (this.previewCropWidth / videoWidth)) : 1;
			const mobileXLeft = this.getBoundedPreviewXLeft(xLeft, maxXLeft, videoWidth);

			this.$refs.playerWrapper.style.transform = `translateX(${this.getPreviewTranslateX(mobileXLeft, videoWidth, frameWidth)}px)`;
		},
		getBoundedPreviewXLeft(xLeft, maxXLeft, videoWidth) {
			const boundedXLeft = Math.max(0, Math.min(xLeft, maxXLeft));

			if (videoWidth <= 0 || boundedXLeft * videoWidth <= CROP_EDGE_SNAP_PX) {
				return 0;
			}

			if ((maxXLeft - boundedXLeft) * videoWidth <= CROP_EDGE_SNAP_PX) {
				return maxXLeft;
			}

			return boundedXLeft;
		},
		getPreviewTranslateX(xLeft, videoWidth, frameWidth) {
			if (videoWidth <= 0 || frameWidth <= 0) {
				return 0;
			}

			const desiredTranslateX = -xLeft * videoWidth;

			const minTranslateX = Math.min(0, frameWidth - videoWidth);

			return Math.max(minTranslateX, Math.min(0, desiredTranslateX));
		},
		downloadCropVideo() {
			window.open(this.cropVideoDownloadUrl, '_blank');
		},
		closeDownloadModal() {
			if (this.cropTaskWsSubscription) {
				ws.unsubscribe(this.cropTaskWsSubscription);
			}
			this.resetClipping();
			this.eventBus.$emit(`modalClose:${this.modalNameDownloadVideo}`);
		},
		showMaxDurationReachedInfo() {
			this.maxDurationReachedInfoVisible = true;
			setTimeout(() => {
				this.maxDurationReachedInfoVisible = false;
			}, 5000);
		},
		async createVideoCrop() {
			try {
				if (!this.isCommunityCropping) {
					this.clipData.graphicId = this.selectedGraphicId;
				}
				this.internalUiStatus = UI_STATUS.GENERATING_VIDEO_CROP;

				const headers = {};
				if (!this.isCommunityCropping) {
					headers['Asport-User-Context-ID'] = document.head.querySelector('meta[name="asport-user-context-id"]').content;
				}

				const data = {
					event: this.eventId,
					...this.clipData,
				};

				// Use currently selected audio track for export.
				if (this.currentAudioTrack?.language) {
					data.audioTrackSelectionFilters = [{
						default: true,
						filter:  'language',
						value:   this.currentAudioTrack.language,
					}];
				}

				const response = this.isCommunityCropping
					? await httpClient.createCommunityVideoCrop(this.eventId, data, { headers })
					: await httpClient.createVideoCrop(this.eventId, data, { headers });

				this.cropVideoStart = Date.now();
				this.cropVideoId = response.data.id;
				this.cropVideoStatus = response.data.status;

				this.cropTaskWsSubscription = ws.subscribe({
					channel:  `eventVideoCrop.${this.cropVideoId}`,
					event:    WS_EVENT.EVENT_VIDEO_CROP_UPDATED,
					callback: this.handleCropUpdated,
				});

				this.cropOvertimeInterval = setInterval(() => {
					// Video takes longer to process than expected
					const timeElapsed = Date.now() - this.cropVideoStart;
					if (timeElapsed > CROP_VIDEO_PROCESSING_OVERTIME) {
						this.internalUiStatus = UI_STATUS.VIDEO_CROP_OVERTIME;
						clearInterval(this.cropOvertimeInterval);
					}
				}, 1000);
			} catch (error) {
				this.internalUiStatus = UI_STATUS.VIDEO_CROP_FAILURE;
				this.$toast.error(this.$I18n.trans('commons.something_went_wrong'));
			}
		},
		async storeEmailNotification() {
			this.emailNotificationSaved = false;
			this.emailNotificationError = false;
			try {
				const response = await httpClient.createVideoCropNotification(this.cropVideoId, this.email);

				if (response.status === 200) {
					this.emailNotificationSaved = true;
					this.internalUiStatus = UI_STATUS.EMAIL_NOTIFICATION_REQUESTED;

					if (this.cropTaskWsSubscription) {
						ws.unsubscribe(this.cropTaskWsSubscription);
					}
				} else {
					throw new Error('Unable to store email notification: ' + response.data);
				}
			} catch (e) {
				this.emailNotificationError = true;
			}
		},
		async handleCropUpdated(update) {
			this.cropVideoStatus = update.eventVideoCrop.status;

			// Get a shortlink for the video crop
			if ([VideoCropStatus.AVAILABLE, VideoCropStatus.PROCESSING].includes(this.cropVideoStatus)) {
				this.cropVideoDownloadUrl = await getShortLinkForMoment(this.cropVideoId);
				this.prefetchVideoForSharing();
			}

			// Keep ws connection open until failed or processed
			if ([VideoCropStatus.AVAILABLE, VideoCropStatus.PROCESSING, VideoCropStatus.FAILED].includes(this.cropVideoStatus)) {
				ws.unsubscribe(this.cropTaskWsSubscription);
			}
		},
		async prefetchVideoForSharing() {
			if (!this.isCommunityCropping || !this.isFileSharingSupported) {
				return;
			}

			try {
				const response = await fetch(this.cropVideoDownloadUrl, {
					credentials: 'omit',
				});
				this.videoBlob = await response.blob();
				this.videoReadyForSharing = true;
			} catch (error) {
				console.error('Error pre-fetching video:', error);
				this.videoReadyForSharing = false;
			}
		},
		async shareVideo() {
			if (!this.isCommunityCropping || !this.videoReadyForSharing || !this.videoBlob) {
				return;
			}

			try {
				const file = new File([this.videoBlob], 'video.mp4', { type: 'video/mp4' });
				await navigator.share({
					files: [file],
				});
			} catch (error) {
				if (error.name !== 'AbortError') {
					this.$toast.error(this.$I18n.trans('commons.something_went_wrong'));
					console.error('Error sharing video:', error);
				}
			}
		},
		setMaxDuration(durationS) {
			const durationMs = durationS * 1000;
			this.maxClipDuration = Math.floor(Math.min(durationMs, CLIP_MAX_DURATION_MS) / 1000) * 1000;
		},
		handleTabChange(tabKey) {
			this.activeTab = tabKey;
		},
		openHelp() {
			window.eventBus.$emit(`modal:${this.modalNameKeyboardShortcuts}`, KEYBOARD_SHORTCUTS);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.multi-format-tool {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;

	&.is-mobile-layout.is-preview {
		position: fixed;
		z-index: 1000000; // stylelint-disable-line declaration-property-value-allowed-list -- Fake fullscreen preview must sit above the surrounding page.
		inset: 0;
		background-color: var(--one-panel-background-color);
	}
}

.multi-format-tool__header {
	display: flex;
	height: 52px;
	flex-shrink: 0;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	padding: 0 $sp4;

	.multi-format-tool.is-mobile-layout.is-preview & {
		display: none;
	}

	@include size(md-up) {
		height: 70px;
	}
}

.multi-format-tool__title {
	display: flex;
	flex-direction: row;
	align-items: center;
	padding-right: 24px;
	padding-top: 0.6em;
	cursor: pointer;
	font-size: 18px; // stylelint-disable-line sh-waqar/declaration-use-variable, property-disallowed-list
	line-height: 1; // stylelint-disable-line sh-waqar/declaration-use-variable, property-disallowed-list
	margin-bottom: $sp5;

	span { // stylelint-disable-line selector-max-type
		position: relative;

		&::after { // stylelint-disable-line selector-max-type
			content: 'spotlight';
			position: absolute;
			right: -5%;
			top: 105%;
			font-size: 64%; // stylelint-disable-line sh-waqar/declaration-use-variable, property-disallowed-list
			color: var(--color-grey-200);

			body.-t-light & { // stylelint-disable-line
				color: var(--color-grey-600);
			}
		}
	}

	@include size(md-up) {
		font-size: 20px; // stylelint-disable-line sh-waqar/declaration-use-variable, property-disallowed-list
	}
}

.multi-format-tool__main-content {
	position: relative;

	.multi-format-tool.is-mobile-layout.is-preview > & {
		height: 100%;
		padding: 0;
		box-sizing: border-box;
		align-items: center;
		justify-content: center;
	}
}

.multi-format-tool__col {
	&.-main {
		width: 100%;
		min-width: 0;

		.multi-format-tool.is-mobile-layout.is-preview & {
			height: 100%;
			align-items: center;
			justify-content: center;
			flex-grow: 1;
			flex-shrink: 1;
		}
	}
}

.multi-format-tool__loading-spinner {
	width: 100%;
	margin: 0 auto;
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100%;
}

.multi-format-tool__tool-w {
	flex-grow: 1;
	flex-shrink: 1;
	min-height: 0;

	.multi-format-tool.is-mobile-layout & {
		flex-grow: 0;
		flex-shrink: 0;
	}

	.multi-format-tool.is-mobile-layout.is-preview & {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 100%;
	}

	@include mobile-portrait {
		padding: 0;
	}
}

.multi-format-tool__graphic-select {
	margin-bottom: 0;
}

.multi-format-tool__moment-title + .multi-format-tool__graphic-select {
	margin-top: $sp4;
}

.multi-format-tool__section-title {
	margin-bottom: $sp4;
}

.multi-format-tool__player-content {
	position: relative;
	display: flex;
	justify-content: center;
	overflow: hidden;

	.multi-format-tool.is-mobile-layout.is-preview & {
		width: 100%;
		max-width: none;
		height: 100%;
		max-height: 100%;
		margin: 0 auto;
		overflow: hidden;
		flex-grow: 0;
	}
}

.multi-format-tool__player-w {
	position: relative;
	display: flex;
	flex-direction: column;
	will-change: transform;
	width: 100%;
	height: 100%;

	.multi-format-tool.is-mobile-layout.is-preview & {
		position: absolute;
		top: 0;
		left: 0;
		height: 100%;
		max-width: none;
	}
}

.multi-format-tool__preview-container {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: var(--one-panel-background-color);
	mask-size: 100% 100%;
	mask-position: center;
	mask-repeat: no-repeat;

	.multi-format-tool.is-mobile-layout.is-preview & {
		display: none;
	}
}

.multi-format-tool__in-progress-info {
	text-align: center;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: $sp5;
}

.multi-format-tool__preview-graphic-container {
	position: absolute;
	bottom: 0;
	left: 50%;
	transform: translateX(-50%);
	right: 0;
	top: 0;

	.multi-format-tool.is-mobile-layout.is-preview & {
		width: 100% !important; // stylelint-disable-line declaration-no-important -- Mobile preview is already clipped to the selected aspect-ratio viewport.
	}
}

.multi-format-tool__preview-graphic {
	height: auto;
	position: absolute;
}

.multi-format-tool__preview-close {
	position: fixed;
	z-index: 1000001; // stylelint-disable-line declaration-property-value-allowed-list -- Keep preview close button above fake fullscreen video.
	top: calc(env(safe-area-inset-top, 0px) + #{$sp4});
	right: $sp4;
}

.multi-format-tool__overtime-message {
	text-align: left;
}

.multi-format-tool__overtime-email {
	margin-top: $sp4;
	margin-bottom: 0;
}

.multi-format-tool__notification-message {
	display: flex;
	align-items: flex-start;
	flex-direction: row;
	text-align: left;

	.v2-icon {
		margin-right: $sp4;
	}
}

.multi-format-tool__copy-link {
	margin-top: $sp6;

	&::v-deep .v2-input-text {
		margin-bottom: 0;
	}
}

.multi-format-tool__tab-nav {
	flex-grow: 1;
	min-height: 0;
	overflow: hidden;

	&::v-deep .tab,
	&::v-deep .tab__wrapper,
	&::v-deep .tab__content {
		height: 100%;
		min-height: 0;
	}

	&::v-deep .tab,
	&::v-deep .tab__wrapper {
		display: flex;
		flex-direction: column;
	}

	&::v-deep .tab__content {
		overflow: hidden;
	}

	.multi-format-tool.is-mobile-layout.is-preview & {
		position: fixed;
		z-index: 1000001; // stylelint-disable-line declaration-property-value-allowed-list -- Keep preview controls above fake fullscreen video.
		top: auto;
		bottom: calc(env(safe-area-inset-bottom, 0px) + #{$sp4});
		left: $sp4;
		right: $sp4;
		height: auto;
		margin-top: 0;
		overflow: visible;
		flex-grow: 0;

		&::v-deep .tab,
		&::v-deep .tab__wrapper,
		&::v-deep .tab__content {
			height: auto;
		}
	}
}

.multi-format-tool__sidebar-content {
	display: flex;
	flex-direction: column;
	gap: $sp2;
	height: 100%;
	min-height: 0;
	overflow-y: auto;
}

.multi-format-tool__controls {
	padding: $sp5 $sp4;

	.multi-format-tool.is-mobile-layout.is-preview & {
		padding: 0;
	}
}

.multi-format-tool__player-keyframe-lane {
	padding-top: $sp3;
	padding-left: $sp4;
	padding-right: $sp4;
}

.multi-format-tool__keyframe-w {
	@include z-index('tag-timeline', 'tagging-tool');
	position: relative;
	display: flex;
	flex-direction: column;
	gap: $sp4;
}

.multi-format-tool__mobile-actions {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	gap: $sp4;
}

.multi-format-tool__workflow-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: $sp4;
	width: 100%;
}

.multi-format-tool__workflow-status {
	min-height: 0;
	justify-content: flex-end;
}

.multi-format-tool__workflow-actions {
	width: auto;
	margin-left: auto;
	flex-grow: 1;
}

.multi-format-tool__rotate-message {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: $sp4;
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	text-align: center;
	font-size: 24px; // stylelint-disable-line
	color: var(--color-white);
}

.multi-format-tool__help-button {
	font-size: 24px; // stylelint-disable-line
	font-weight: 300; // stylelint-disable-line
}

</style>
